import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import memoize from 'lodash/memoize';
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  getHumanReadableNameFromPaymentMethod,
  getIdentifier,
  isPaidByCreditCard,
} from '@atlassian/cssxp-utils-invoice';
import {
  FetchInvoicesForTransactionAccountQuery,
  Invoice,
  InvoicePaymentMethod,
  FetchInvoicesForTransactionAccountQueryVariables,
  Refunds,
  SortDirection,
  InvoiceStatus,
} from '@css-xp/graphtypes';
//@ts-ignore
import InvoiceIcon from '@css-xp/base/assets/img/icon-invoice.svg';
import {
  FormattedCurrency,
  FormattedDate,
  OpenSidebarRow,
  ColGroup,
  SearchResultColumn,
  SearchResultsTable,
  TableBody,
  TableHead,
  SimpleTD,
  getTextAndTitle,
  useSidebar,
  PaymentErrorLozenge,
} from '@css-xp/base';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { fetchInvoicesForTransactionAccount } from './queries/fetch-invoices-for-transaction-account';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import RefundChildProductRow from '../../../invoices/src/components/invoice-child-refund-row';
import isNil from 'lodash/isNil';
// import {
//     Enabled,
//     FeatureSwitch,
// } from '../../common/security/containers/feature-switch';
// import { ENABLE_CREDIT_NOTES } from '../../../../../common/feature-flag-keys';

export const PAGE_TAB_SEARCH_RESULTS_LOAD_MORE = 15;

interface Props {
  id: string;
  label?: string;
}

export const getIsFreeInvoice = memoize((paymentMethod?: InvoicePaymentMethod | null) => {
  return paymentMethod === InvoicePaymentMethod.NOPAYMENT;
});

export const getPaymentMethod = memoize((paymentMethod?: InvoicePaymentMethod | null) => {
  return paymentMethod && !getIsFreeInvoice(paymentMethod)
    ? getHumanReadableNameFromPaymentMethod(paymentMethod)
    : 'n/a';
});

export const getInvoiceCardNumber = memoize(
  (invoice) => {
    return isPaidByCreditCard(invoice.paymentMethod) && invoice.creditCardMaskedNumber ? (
      <span className="credit-card-number">{invoice.creditCardMaskedNumber}</span>
    ) : (
      <span className="not-applicable">n/a</span>
    );
  },
  (...args) => {
    return JSON.stringify(args);
  }
);

const isPaymentIssues = (invoice: Invoice): boolean => {
  const DUNNING_PAYMENT_STATUS = [
    InvoiceStatus.OPEN,
    InvoiceStatus.DRAFT,
    InvoiceStatus.PAYMENT_DEFERRED,
    InvoiceStatus.VOID,
  ];
  if (invoice.dunningStatus?.length && invoice.status) {
    return DUNNING_PAYMENT_STATUS.includes(invoice.status);
  } else {
    return false;
  }
};

const getColumns = memoize((isSidebarOpen: boolean): SearchResultColumn[] => {
  const columns = [
    {
      name: 'Invoice Id',
      customClassNames: isSidebarOpen ? 'col-40' : 'col-30',
    },
    {
      name: 'PO Number',
      customClassNames: 'col-10',
    },
    {
      name: 'Billing domain',
      customClassNames: 'col-20',
    },
    {
      name: 'Billing contact',
      customClassNames: 'col-10',
    },
    {
      name: !isSidebarOpen ? 'Payment method' : undefined,
      customClassNames: !isSidebarOpen ? 'col-10' : undefined,
    },
    {
      name: 'Card number',
      customClassNames: 'col-20',
    },
    {
      name: 'Date due/paid',
      customClassNames: 'col-10',
    },
    {
      name: 'Date created',
      customClassNames: 'col-10',
    },
    {
      name: 'Amount',
      customClassNames: 'col-10',
    },
    {
      name: '',
    },
  ];

  return columns;
});

export const defaultSearchOptions: FetchInvoicesForTransactionAccountQueryVariables['searchOptions'] = {
  first: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
  sort: {
    sortFields: ['createdDate', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
  },
};

const InvoiceTabTxA = React.memo(({ id, label }: Props) => {
  const { isSidebarOpen, payload } = useSidebar();
  const { fetchMore, loading, error, data } = useQuery<
    FetchInvoicesForTransactionAccountQuery,
    FetchInvoicesForTransactionAccountQueryVariables
  >(fetchInvoicesForTransactionAccount, {
    variables: {
      id: id,
      searchOptions: defaultSearchOptions,
    },
    displayName: 'fetchInvoicesForTransactionAccount',
    notifyOnNetworkStatusChange: true,
  });

  //variables to use for paging and populating our template
  const totalCount = data?.transactionAccount?.invoices?.totalCount ?? 0;
  const hasNoResults = !loading && totalCount === 0;
  const nodes = data?.transactionAccount?.invoices?.nodes;
  const resultsCount = nodes?.length ?? 0;

  const handleOnLoadMoreClick = () => {
    const cursor = data?.transactionAccount?.invoices?.pageInfo?.endCursor;
    fetchMore({
      query: fetchInvoicesForTransactionAccount,
      variables: {
        id: id,
        searchOptions: {
          ...defaultSearchOptions,
          cursor,
        },
      },
      //@ts-ignore
      updateQuery: (
        previousResult,
        { fetchMoreResult }: { fetchMoreResult: FetchInvoicesForTransactionAccountQuery }
      ) => {
        if (
          fetchMoreResult?.transactionAccount?.invoices?.nodes &&
          previousResult?.transactionAccount?.invoices?.nodes
        ) {
          fetchMoreResult.transactionAccount.invoices.nodes = [
            ...previousResult.transactionAccount.invoices.nodes,
            ...fetchMoreResult.transactionAccount.invoices?.nodes,
          ];
        }
        return fetchMoreResult;
      },
    });
  };
  return (
    <div className="transaction-account-tab" data-testid="transaction-account-invoice-tab">
      <SearchResultsTable
        id="invoices"
        isLoading={loading}
        error={error?.toString()}
        heading={label}
        noResults={hasNoResults}
        loadMore={{
          showLoadMore: totalCount !== 0 && resultsCount < totalCount,
          totalCount: totalCount,
          resultsCount: resultsCount,
          onLoadMoreClick: handleOnLoadMoreClick,
        }}
        entityName="invoices"
      >
        <ColGroup columns={getColumns(isSidebarOpen)} />
        <TableHead columns={getColumns(isSidebarOpen)} />
        <TableBody>
          {/*@ts-ignore*/}
          {(nodes ?? []).map((invoice: Invoice, index: number) => {
            const {
              id,
              invoiceNumber,
              billingContactName,
              customerDomain,
              dueDate,
              grandTotal,
              paymentCurrency,
              createdDate,
              paymentMethod,
              sourceSystem,
              refunds,
            } = invoice;

            const identifier = getIdentifier({
              id,
              invoiceNumber,
              sourceSystem,
            });
            const rowPayload = {
              invoiceId: identifier,
            };

            return (
              <>
                <OpenSidebarRow
                  key={`invoice-table-row-${index}`}
                  sidebar={GloballyAllowedSidebar.INVOICE_PREVIEW}
                  payload={rowPayload}
                  isSelected={false}
                  data-testid={`invoice-table-row-${index}`}
                >
                  <td title={getTextAndTitle(id)}>
                    <Link
                      to={`/invoices/${encodeURIComponent(identifier)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="avatar-container"
                    >
                      {/*// @ts-ignore */}
                      <img src={InvoiceIcon} className="avatar medium" />
                      {identifier}
                      {isPaymentIssues(invoice) && <PaymentErrorLozenge />}
                    </Link>
                  </td>
                  <td>{!isNil(invoice?.purchaseOrderNumber) ? invoice.purchaseOrderNumber : 'N/A'}</td>
                  <td>{customerDomain ?? <span className="not-applicable">n/a</span>}</td>
                  <td>{billingContactName ?? <span className="not-applicable">n/a</span>}</td>
                  <SimpleTD
                    text={getPaymentMethod(paymentMethod)}
                    className={classNames({
                      'not-applicable': getIsFreeInvoice(paymentMethod) || !paymentMethod,
                    })}
                  />
                  <td>{getInvoiceCardNumber(invoice)}</td>

                  <td>{dueDate ? <FormattedDate date={dueDate} /> : <span className="not-applicable">n/a</span>}</td>
                  <td>
                    {createdDate ? <FormattedDate date={createdDate} /> : <span className="not-applicable">n/a</span>}
                  </td>

                  <td className="invoice-amount-cell">
                    {!getIsFreeInvoice(paymentMethod) && grandTotal ? (
                      <FormattedCurrency value={grandTotal} currency={paymentCurrency} showCurrencyCode />
                    ) : (
                      'Free'
                    )}
                  </td>
                </OpenSidebarRow>

                {/* <FeatureSwitch flag={ENABLE_CREDIT_NOTES}>
                <Enabled> */}
                {!isEmpty(refunds) &&
                  //@ts-ignore
                  (refunds ?? []).map((ccpCreditNote: Refunds) => {
                    const refundPayload = {
                      refundId: ccpCreditNote?.creditNoteNumber || ccpCreditNote?.id,
                    };
                    const isChildRowSelected = isSidebarOpen && isEqual(refundPayload, payload);
                    return (
                      <RefundChildProductRow
                        key={ccpCreditNote.id}
                        parentColumns={9}
                        hideActions={false}
                        refund={ccpCreditNote}
                        isSelected={isChildRowSelected}
                      />
                    );
                  })}
                {/* </Enabled>
                </FeatureSwitch> */}
              </>
            );
          })}
        </TableBody>
      </SearchResultsTable>
    </div>
  );
});

export default InvoiceTabTxA;
